/**
 * 选区动作（M2-07 / DESIGN.md §6.2）。
 *
 * 全部/取消选择/反选。选区是应用层会话状态（不进文档、不进历史——
 * 与 M2-01 的扫描无冲突：这里不碰 Document）。依赖以闭包注入，
 * 测试不需要完整的 AppStore。
 */
import { SelectionMask } from '../../engine/selection/mask';
import { invert } from '../../engine/selection/ops';
import { PHOTOSHOP_ACTION_SHORTCUTS } from './keymap';
import type { ActionRegistry } from './registry';

export interface SelectionDeps {
  readonly selection: () => SelectionMask | null;
  readonly setSelection: (mask: SelectionMask | null) => void;
  /** 当前文档尺寸；无文档时 null。 */
  readonly documentSize: () => { readonly width: number; readonly height: number } | null;
  /**
   * 扩大选区/选取相似：跟 PS 一致，没有对话框，直接执行、直接用当前魔棒
   * 容差。两个都要看合成像素，注入完整实现而不是原始像素——这两个动作
   * 需要 `store.document`/`store.tiles`/魔棒容差三样，硬塞进 `SelectionDeps`
   * 反而破坏了它原本"只关心 SelectionMask"的最小依赖面。留空（旧测试的
   * 调用点）时这两个动作照常注册但静默 no-op，不强制所有调用点都要传。
   */
  readonly grow?: () => Promise<boolean>;
  readonly similar?: () => Promise<boolean>;
  /**
   * 选择主体：传统 CV 路线（全局颜色对比度显著性，不含 AI/ONNX），跟
   * grow/similar 一样没有对话框、直接执行。跟 grow/similar 的区别是
   * 不需要预先有选区——真实 PS 一致，"选择主体"是从零开始生成一个选区。
   * 留空时静默 no-op，同 grow/similar 的既有约定。
   */
  readonly subject?: () => Promise<boolean>;
  /**
   * 色彩范围…：打开对话框，不像 grow/similar 那样直接执行——需要用户
   * 先点画布取样参考色、调容差看实时预览，跟 grow/similar"没有对话框"
   * 的既有惯例不是同一类，更接近"选择→修改"那五项的 openDialog idiom。
   * 不需要预先有选区（跟 select.subject 一样，真实 PS 一致）。留空时
   * 静默 no-op，同 grow/similar/subject 的既有约定。
   */
  readonly colorRange?: () => void;
}

export function registerSelectionActions(registry: ActionRegistry, deps: SelectionDeps): void {
  // 取消选择时记住选区，供「重新选择」找回——只记「取消选择」这一个动作
  // 清的那次，不记其它任何把 selection 设为 null 的路径（比如换文档），
  // 那些路径下「找回上一次选区」没有意义
  let lastDeselected: SelectionMask | null = null;

  registry.register({
    id: 'select.all',
    title: '全部',
    group: '选择',
    section: 0,
    order: 0,
    shortcut: PHOTOSHOP_ACTION_SHORTCUTS['select.all'],
    enabled: (s) => s.hasDocument,
    run: () => {
      const size = deps.documentSize();
      if (size !== null) deps.setSelection(SelectionMask.all(size.width, size.height));
    },
  });

  registry.register({
    id: 'select.deselect',
    title: '取消选择',
    group: '选择',
    section: 0,
    order: 1,
    shortcut: PHOTOSHOP_ACTION_SHORTCUTS['select.deselect'],
    enabled: (s) => s.hasSelection,
    run: () => {
      const current = deps.selection();
      if (current !== null) lastDeselected = current;
      deps.setSelection(null);
    },
  });

  registry.register({
    id: 'select.reselect',
    title: '重新选择',
    group: '选择',
    section: 0,
    order: 2,
    shortcut: PHOTOSHOP_ACTION_SHORTCUTS['select.reselect'],
    enabled: (s) => s.hasDocument && !s.hasSelection && lastDeselected !== null,
    run: () => {
      if (lastDeselected !== null) deps.setSelection(lastDeselected);
    },
  });

  registry.register({
    id: 'select.invert',
    title: '反选',
    group: '选择',
    section: 0,
    order: 3,
    shortcut: PHOTOSHOP_ACTION_SHORTCUTS['select.invert'],
    enabled: (s) => s.hasDocument,
    run: () => {
      const size = deps.documentSize();
      if (size === null) return;
      const current = deps.selection() ?? SelectionMask.empty(size.width, size.height);
      deps.setSelection(invert(current));
    },
  });

  registry.register({
    id: 'select.grow',
    title: '扩大选区',
    group: '选择',
    section: 2,
    order: 0,
    enabled: (s) => s.hasSelection,
    run: () => {
      void deps.grow?.();
    },
  });

  registry.register({
    id: 'select.similar',
    title: '选取相似',
    group: '选择',
    section: 2,
    order: 1,
    enabled: (s) => s.hasSelection,
    run: () => {
      void deps.similar?.();
    },
  });

  registry.register({
    id: 'select.subject',
    title: '选择主体',
    group: '选择',
    section: 2,
    order: 2,
    // 跟 grow/similar 不同：不需要预先有选区，真实 PS 一致
    enabled: (s) => s.hasDocument,
    run: () => {
      void deps.subject?.();
    },
  });

  registry.register({
    id: 'select.colorRange',
    title: '色彩范围…',
    group: '选择',
    section: 2,
    order: 3,
    enabled: (s) => s.hasDocument,
    run: () => {
      deps.colorRange?.();
    },
  });
}
